import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import React, { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { ICandidate, ICandidateResume, IMappingFormSettingsResponse } from 'wisestep-api';

import { WsDatePicker } from '../../components/WsDatePicker';
import { WsSelect, WsSelectObj } from '../../components/WsSelect';
import { formatPhoneNumber } from '../../helper/PhonenumberFormat';
import ISubmissionSettingContext from '../../types/ISubmissionSettingContext';
import ISubmissionSettingsResponse from '../../types/ISubmissionSettingsResponse';
import WsColors from '../../WsColors';
import { ControlledTextInput } from '../ControlledTextInput';
import ControlledWsButtonGroup from '../ControlledWsButtonGroup/ControlledWsButtonGroup';
import { Label } from '../Label';
import ControlledLanguageInput from '../LanguageInput/ControlledLanguageInput';
import ControlledMessengerInput from '../MessengerInput/ControlledMessengerInput';
import ControlledSocialLinkInput from '../SocialLinkInput/ControlledSocialLinkInput';
import { TagsInput } from '../TagsInput';
import WsFileUpload from '../WsFileUpload/WsFileUpload';
import { WsTypoColors, WsTypography, WsTypoVariants } from '../WsTypography';

import RenderSelectInput from './RenderSelectInput';
import RenderTextInput, { GridItem } from './RenderTextInput';

const LinkTag = styled(Link)(() => ({
  color: WsColors.TheBlues500,
  cursor: 'pointer',
  float: 'right',
}));

const FormTagsInput = styled(TagsInput)(() => ({
  '.MuiOutlinedInput-root': {
    padding: '4.5px 8px',
    margin: 0,
  },
}));

interface IPersonalInfo {
  candidate: ICandidate;
  showTitle?: boolean;
  submissionSetting?: ISubmissionSettingsResponse | ISubmissionSettingContext | null;
  purpose?: 'submission' | 'mapping';
  onUpload?: (args: File[]) => void;
  resumeAction?: (args1: string, args2?: string | null) => void;
  mappingFormSettings?: IMappingFormSettingsResponse;
  CVOptions?: WsSelectObj[];
  onEditClick?: () => void;
}

const PersonalInfo: FC<IPersonalInfo> = ({
  candidate,
  showTitle = true,
  submissionSetting,
  purpose = 'submission',
  onUpload,
  resumeAction,
  mappingFormSettings,
  CVOptions,
  onEditClick = () => {},
}) => {
  const {
    formState: { errors },
    watch,
  } = useFormContext();
  const [showExtraMailField, setShowExtraMailField] = useState<boolean>(false);
  const [showExtraPhoneField, setShowExtraPhoneField] = useState<boolean>(false);
  const watchEmail = watch('email');
  const watchPhone = watch('phone');
  const watchAlternateEmails = watch('alternateEmails');
  const watchAlternatePhones = watch('alternatePhoneNumbers');

  useEffect(() => {
    !isEmpty(errors.alternatePhoneNumbers) && setShowExtraPhoneField(true);
    !isEmpty(errors.alternateEmails) && setShowExtraMailField(true);
  }, [errors, showExtraPhoneField]);

  const emailOptions = [
    ...(candidate?.email ? [{ label: candidate.email, value: candidate.email }] : []),
    ...(candidate?.alternateEmails?.map((email) => ({ label: email, value: email })) ?? []),
  ];

  const phoneNumberOptions = [
    ...(candidate?.phoneNumber
      ? [
          {
            label: formatPhoneNumber(
              candidate.currentLocation?.countryCode ?? '',
              candidate.phoneNumber
            ),
            value: formatPhoneNumber(
              candidate.currentLocation?.countryCode ?? '',
              candidate.phoneNumber.replace(/\D/g, '')
            ),
          },
        ]
      : []),
    ...(candidate?.alternatePhoneNumbers?.map((phone) => ({
      label: formatPhoneNumber(candidate.currentLocation?.countryCode ?? '', phone),
      value: formatPhoneNumber(
        candidate.currentLocation?.countryCode ?? '',
        phone.replace(/\D/g, '')
      ),
    })) ?? []),
  ];

  const filterOptions = (
    options: { label: string; value: string }[],
    selected: string,
    alternate: string | string[]
  ) =>
    options.filter(
      (option) => option.value !== selected && ![alternate].flat().includes(option.value)
    );

  const filterEmailOptions = filterOptions(emailOptions, watchEmail, watchAlternateEmails);
  const filterPhoneOptions = filterOptions(phoneNumberOptions, watchPhone, watchAlternatePhones);

  const GenderOptions = [
    {
      label: 'Male',
      value: 'MALE',
    },
    {
      label: 'Female',
      value: 'FEMALE',
    },
    {
      label: 'Not Specified',
      value: 'NOT_SPECIFIED',
    },
  ];
  const formLabel = (label: string, isRequired: boolean) => label + (isRequired ? '*' : '');

  const fields = [
    {
      condition: !submissionSetting?.name?.hidden,
      render: () => (
        <RenderTextInput
          name="name"
          labelText={formLabel('Name ', submissionSetting?.name?.required ?? false)}
          placeholder="Enter Your Name"
        />
      ),
    },
    {
      condition: !submissionSetting?.email?.hidden,
      render: () => (
        <RenderSelectInput
          name="email"
          label={formLabel('Email Address ', submissionSetting?.email?.required ?? false)}
          options={[{ label: watchEmail, value: watchEmail }, ...filterEmailOptions]}
          defaultValue={candidate?.email ?? ''}
          onEditClick={onEditClick}
        />
      ),
    },
    {
      condition: !submissionSetting?.phoneNumber?.hidden,
      render: () => (
        <RenderSelectInput
          name="phone"
          label={formLabel('Phone Number ', submissionSetting?.phoneNumber?.required ?? false)}
          options={[{ label: watchPhone, value: watchPhone }, ...filterPhoneOptions]}
          defaultValue={
            formatPhoneNumber(
              candidate.currentLocation?.countryCode ?? '',
              candidate?.phoneNumber ?? ''
            ) ?? ''
          }
          onEditClick={onEditClick}
        />
      ),
    },
    {
      condition: !submissionSetting?.alternateEmail?.hidden,
      render: () => (
        <RenderSelectInput
          name="alternateEmails"
          label={formLabel(
            'Alternate Email Address ',
            submissionSetting?.alternateEmail?.required ?? false
          )}
          options={[
            { label: watchAlternateEmails, value: watchAlternateEmails },
            ...filterEmailOptions,
          ]}
          defaultValue={candidate?.alternateEmails?.[0] ?? ''}
        />
      ),
    },
    {
      condition: !submissionSetting?.alternatePhoneNumber?.hidden,
      render: () => (
        <RenderSelectInput
          name="alternatePhoneNumbers"
          label={formLabel(
            'Alternate Phone Number ',
            submissionSetting?.alternatePhoneNumber?.required ?? false
          )}
          options={[
            { label: watchAlternatePhones, value: watchAlternatePhones },
            ...filterPhoneOptions,
          ]}
          defaultValue={
            formatPhoneNumber(
              candidate.currentLocation?.countryCode ?? '',
              candidate?.alternateEmails?.[0] ?? ''
            ) ?? ''
          }
        />
      ),
    },
    {
      condition: !submissionSetting?.passportNumber?.hidden,
      render: () => (
        <RenderTextInput
          name="passportNumber"
          labelText={formLabel(
            'Passport Number',
            submissionSetting?.passportNumber?.required ?? false
          )}
          placeholder="Enter Passport Number"
        />
      ),
    },
    {
      condition: !submissionSetting?.passportType?.hidden,
      render: () => (
        <RenderTextInput
          name="passportType"
          labelText={formLabel('Passport Type', submissionSetting?.passportType?.required ?? false)}
          placeholder="Enter Passport Type"
        />
      ),
    },
    {
      condition: !submissionSetting?.dob?.hidden,
      render: () => (
        <GridItem>
          <Grid xs={8.5}>
            <Label>{formLabel('Date of Birth ', submissionSetting?.dob?.required ?? false)}</Label>
            <WsDatePicker
              name="dob"
              label=""
              disableFuture
              required={submissionSetting?.dob?.required}
            />
          </Grid>
        </GridItem>
      ),
    },
    {
      condition: !submissionSetting?.age?.hidden,
      render: () => (
        <RenderTextInput
          name="age"
          labelText={formLabel('Age ', submissionSetting?.age?.required ?? false)}
          placeholder="Enter Candidate Age"
          type="number"
        />
      ),
    },
    {
      condition: !submissionSetting?.gender?.hidden,
      render: () => (
        <RenderSelectInput
          name="gender"
          label={formLabel('Gender ', submissionSetting?.gender?.required ?? false)}
          options={GenderOptions}
          defaultValue={candidate?.gender ?? 'NOT_SPECIFIED'}
        />
      ),
    },
    {
      condition: !submissionSetting?.postalAddress?.hidden,
      render: () => (
        <RenderTextInput
          name="postalAddress"
          labelText={formLabel(
            'Postal Address ',
            submissionSetting?.postalAddress?.required ?? false
          )}
          placeholder="Enter Postal Address"
        />
      ),
    },
    {
      condition: !submissionSetting?.ssnNumber?.hidden,
      render: () => (
        <RenderTextInput
          name="ssnNumber"
          labelText={formLabel('SSN Number ', submissionSetting?.ssnNumber?.required ?? false)}
          placeholder="Enter SSN Number"
        />
      ),
    },
    {
      condition: !submissionSetting?.panCard?.hidden,
      render: () => (
        <RenderTextInput
          name="panCard"
          labelText={formLabel('PAN Card ', submissionSetting?.panCard?.required ?? false)}
          placeholder="Enter PAN Card Number"
        />
      ),
    },
    {
      condition: !submissionSetting?.aadharNumber?.hidden,
      render: () => (
        <RenderTextInput
          name="aadharNumber"
          labelText={formLabel(
            'Aadhar Number ',
            submissionSetting?.aadharNumber?.required ?? false
          )}
          placeholder="Enter Aadhar Number"
        />
      ),
    },
    {
      condition: !submissionSetting?.hasVeteranStatus?.hidden,
      render: () => (
        <GridItem>
          <ControlledWsButtonGroup
            name="hasVeteranStatus"
            inputTitle="Veteran Status"
            buttonsList={['Yes', 'No']}
            showMandatorySymbol={submissionSetting?.hasVeteranStatus?.required ?? false}
          />
        </GridItem>
      ),
    },
    {
      condition: !submissionSetting?.securityClearance?.hidden,
      render: () => (
        <GridItem>
          <ControlledWsButtonGroup
            name="securityClearance"
            inputTitle="Security Clearance"
            buttonsList={['Yes', 'No']}
            showMandatorySymbol={submissionSetting?.securityClearance?.required ?? false}
          />
        </GridItem>
      ),
    },
    {
      condition: !submissionSetting?.personalWebsite?.hidden,
      render: () => (
        <RenderTextInput
          name="personalWebsite"
          labelText={formLabel(
            'Personal Website ',
            submissionSetting?.personalWebsite?.required ?? false
          )}
          placeholder="Enter Personal Website"
        />
      ),
    },
    {
      condition: !submissionSetting?.uploadCV?.hidden,
      render: () => (
        <GridItem>
          <Grid container display="flex" spacing={2} alignItems="center">
            <Grid item xs={8.5}>
              <WsSelect
                name="cv"
                label="CV"
                selectArray={CVOptions ?? []}
                defaultValue="0"
                showMandatorySymbol={submissionSetting?.uploadCV?.required}
              />
            </Grid>
            {!isEmpty(CVOptions) && (
              <Grid container item xs={3.5} mt={6} columnSpacing={2}>
                <Grid item>
                  <FileDownloadOutlinedIcon
                    onClick={() => resumeAction && resumeAction('download')}
                    sx={{ color: WsColors.TheBlues500, fontSize: 24, cursor: 'pointer' }}
                  />
                </Grid>
                <Grid item>
                  <VisibilityOutlinedIcon
                    onClick={() => resumeAction && resumeAction('view')}
                    sx={{ color: WsColors.TheBlues500, cursor: 'pointer', fontSize: 24 }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </GridItem>
      ),
    },
    {
      condition: !submissionSetting?.messengerId?.hidden,
      render: () => (
        <GridItem>
          <Grid item xs={8.5}>
            <ControlledMessengerInput
              name="messengerLinks"
              labelText={formLabel(
                'Messenger ID ',
                submissionSetting?.messengerId?.required ?? false
              )}
            />
          </Grid>
        </GridItem>
      ),
    },
    {
      condition: !submissionSetting?.linkedInURL?.hidden,
      render: () => (
        <GridItem>
          <Grid item xs={8.5}>
            <ControlledSocialLinkInput
              name="socialLinks"
              labelText={formLabel(
                'Social Links ',
                submissionSetting?.linkedInURL?.required ?? false
              )}
            />
          </Grid>
        </GridItem>
      ),
    },
    {
      condition: !submissionSetting?.skills?.hidden,
      render: () => (
        <GridItem>
          <Grid xs={8.5}>
            <Label>{formLabel('Skills ', submissionSetting?.skills?.required ?? false)}</Label>
            <FormTagsInput
              name="skills"
              placeholder="Enter Skills"
              multiple
              initialValue={watch('skills') ?? []}
            />
          </Grid>
        </GridItem>
      ),
    },
    // {
    //   condition: !submissionSetting?.source?.hidden,
    //   render: () => (
    //     <RenderTextInput
    //       name="source"
    //       labelText={formLabel('Source ', submissionSetting?.source?.required ?? false)}
    //       placeholder="Enter source"
    //     />
    //   ),
    // },
    {
      condition: !submissionSetting?.candidateID?.hidden,
      render: () => (
        <RenderTextInput
          name="candidatePersonalId"
          labelText={formLabel('Candidate ID ', submissionSetting?.candidateID?.required ?? false)}
          placeholder="Enter Candidate ID"
        />
      ),
    },
    {
      condition: !submissionSetting?.languagesKnown?.hidden,
      render: () => (
        <Grid xs={8} pl={3}>
          <ControlledLanguageInput
            name="candidateLanguages"
            initialValue={[{ languageName: '', fluencyLevel: '' }]}
          />
        </Grid>
      ),
    },
    {
      condition: !submissionSetting?.notes?.hidden,
      render: () => (
        <RenderTextInput
          name="customNote"
          labelText={formLabel('Notes ', submissionSetting?.notes?.required ?? false)}
          placeholder="Enter Notes"
        />
      ),
    },
    {
      condition: !submissionSetting?.otherInterviewStage?.hidden,
      render: () => (
        <RenderTextInput
          name="otherInterviewStage"
          labelText={formLabel(
            'Other Interview Stage ',
            submissionSetting?.otherInterviewStage?.required ?? false
          )}
          placeholder="Enter Other Interview Stage"
        />
      ),
    },
  ];

  return (
    <>
      {showTitle && (
        <GridItem sx={{ mt: '12px' }}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
            Personal Info
          </WsTypography>
        </GridItem>
      )}
      {purpose === 'submission' && (
        <>
          {fields.map(
            (field, index) =>
              field.condition && <React.Fragment key={index}>{field.render()}</React.Fragment>
          )}
        </>
      )}
      {purpose === 'mapping' && (
        <>
          {mappingFormSettings?.name.show && (
            <GridItem sx={{ mt: '12px' }}>
              <ControlledTextInput
                name="name"
                labelText="Name *"
                fullWidth
                id="candidate_name"
                placeholder="Enter Candidate Name"
                variant="outlined"
                autoComplete="candidate_name"
                required={true}
              />
            </GridItem>
          )}
          {mappingFormSettings?.email.show && (
            <GridItem>
              <ControlledTextInput
                name="email"
                labelText="Email address *"
                type="email"
                fullWidth
                id="candidate_email"
                placeholder="Enter Candidate Email Address"
                variant="outlined"
                autoComplete="candidate_email"
              />
              {!showExtraMailField && (
                <LinkTag
                  underline="none"
                  onClick={() => setShowExtraMailField(!showExtraMailField)}
                >
                  <Grid container direction="row" alignItems="center">
                    <Grid item>
                      <AddIcon sx={{ height: '20px', marginTop: '7px' }} />
                    </Grid>
                    <Grid item>&nbsp;&nbsp; Add More</Grid>
                  </Grid>
                </LinkTag>
              )}
            </GridItem>
          )}
          <GridItem>
            {showExtraMailField && mappingFormSettings?.alternateEmails.show && (
              <>
                <FormTagsInput
                  name="alternateEmails"
                  placeholder={formLabel(
                    'Enter Candidate Additional Email Address',
                    mappingFormSettings?.alternateEmails.mandatory
                  )}
                  multiple={true}
                  initialValue={[]}
                  required={mappingFormSettings?.alternateEmails.mandatory}
                />
                <LinkTag
                  underline="none"
                  onClick={() => setShowExtraMailField(!showExtraMailField)}
                >
                  <Grid container direction="row" alignItems="center">
                    <Grid item>
                      <RemoveIcon sx={{ height: '20px', marginTop: '7px' }} />
                    </Grid>
                    <Grid item>Hide</Grid>
                  </Grid>
                </LinkTag>
              </>
            )}
          </GridItem>
          {mappingFormSettings?.phoneNumber.show && (
            <GridItem>
              <ControlledTextInput
                name="phone"
                labelText={formLabel('Phone Number', mappingFormSettings?.phoneNumber.mandatory)}
                fullWidth
                id="candidate_phone"
                placeholder="Enter Candidate Phone Number"
                variant="outlined"
                autoComplete="candidate_phone"
                required={mappingFormSettings?.phoneNumber.mandatory}
              />
              {!showExtraPhoneField && (
                <LinkTag
                  underline="none"
                  onClick={() => setShowExtraPhoneField(!showExtraPhoneField)}
                >
                  <Grid container direction="row" alignItems="center">
                    <Grid item>
                      <AddIcon sx={{ height: '20px', marginTop: '7px' }} />
                    </Grid>
                    <Grid item>&nbsp;&nbsp; Add More</Grid>
                  </Grid>
                </LinkTag>
              )}
            </GridItem>
          )}
          <GridItem>
            {showExtraPhoneField && mappingFormSettings?.alternatePhoneNumber.show && (
              <>
                <FormTagsInput
                  name="alternatePhoneNumbers"
                  placeholder={formLabel(
                    'Enter Candidate Additional Phone Number',
                    mappingFormSettings?.alternatePhoneNumber.mandatory
                  )}
                  multiple={true}
                  initialValue={[]}
                  required={mappingFormSettings?.alternatePhoneNumber.mandatory}
                />
                <LinkTag
                  underline="none"
                  onClick={() => setShowExtraPhoneField(!showExtraPhoneField)}
                >
                  <Grid container direction="row" alignItems="center">
                    <Grid item>
                      <RemoveIcon sx={{ height: '20px', marginTop: '7px' }} />
                    </Grid>
                    <Grid item>Hide</Grid>
                  </Grid>
                </LinkTag>
              </>
            )}
          </GridItem>
          {mappingFormSettings?.passportNumber.show && (
            <GridItem>
              <ControlledTextInput
                name="passportNumber"
                labelText={formLabel(
                  'Passport Number',
                  mappingFormSettings?.passportNumber.mandatory
                )}
                fullWidth
                id="passportNumber"
                placeholder="Enter Passport Number"
                variant="outlined"
                autoComplete="passportNumber"
                required={mappingFormSettings?.passportNumber.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.passportType.show && (
            <GridItem>
              <ControlledTextInput
                name="passportType"
                labelText={formLabel('Passport Type', mappingFormSettings?.passportType.mandatory)}
                fullWidth
                id="passportType"
                placeholder="Enter Passport Type"
                variant="outlined"
                autoComplete="passportType"
                required={mappingFormSettings?.passportType.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.dob.show && (
            <GridItem>
              <Label>{formLabel('Date of Birth', mappingFormSettings?.dob.mandatory)}</Label>
              <WsDatePicker
                name="dob"
                label=""
                disableFuture={true}
                disabled={false}
                required={mappingFormSettings?.dob.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.age.show && (
            <GridItem>
              <ControlledTextInput
                name="age"
                labelText={formLabel('Age', mappingFormSettings?.age.mandatory)}
                fullWidth
                id="age"
                placeholder={formLabel('Enter Candidate Age', mappingFormSettings?.age.mandatory)}
                variant="outlined"
                autoComplete="age"
                required={mappingFormSettings?.age.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.gender.show && (
            <GridItem>
              <WsSelect
                name="gender"
                label={formLabel('Gender', mappingFormSettings?.gender.mandatory)}
                selectArray={GenderOptions}
                defaultValue="NOT SPECIFIED"
                required={mappingFormSettings?.gender.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.postalAddress.show && (
            <GridItem>
              <ControlledTextInput
                name="postalAddress"
                labelText={formLabel(
                  'Postal Address',
                  mappingFormSettings?.postalAddress.mandatory
                )}
                fullWidth
                id="postalAddress"
                placeholder="Enter Postal Address"
                variant="outlined"
                autoComplete="postalAddress"
                required={mappingFormSettings?.postalAddress.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.ssnNumber.show && (
            <GridItem>
              <ControlledTextInput
                name="ssnNumber"
                labelText={formLabel('SSN Number', mappingFormSettings?.ssnNumber.mandatory)}
                fullWidth
                id="ssnNumber"
                placeholder="Enter SSN Number"
                variant="outlined"
                autoComplete="ssnNumber"
                required={mappingFormSettings?.ssnNumber.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.panCard.show && (
            <GridItem>
              <ControlledTextInput
                name="panCard"
                labelText={formLabel('PAN Card', mappingFormSettings?.panCard.mandatory)}
                fullWidth
                id="panCard"
                placeholder="Enter PAN Card Number"
                variant="outlined"
                autoComplete="panCard"
                required={mappingFormSettings?.panCard.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.aadharCard.show && (
            <GridItem>
              <ControlledTextInput
                name="aadharNumber"
                labelText={formLabel('Aadhar Number', mappingFormSettings?.aadharCard.mandatory)}
                fullWidth
                id="aadharNumber"
                placeholder="Enter Aadhar Number"
                variant="outlined"
                autoComplete="aadharNumber"
                required={mappingFormSettings?.aadharCard.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.hasVeteranStatus.show && (
            <GridItem id="hasVeteranStatus">
              <ControlledWsButtonGroup
                name="hasVeteranStatus"
                inputTitle="Veteran Status"
                buttonsList={['Yes', 'No']}
                showMandatorySymbol={mappingFormSettings?.hasVeteranStatus?.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.securityAllowance.show && (
            <GridItem id="securityClearance">
              <ControlledWsButtonGroup
                name="securityClearance"
                inputTitle="Security Clearance"
                buttonsList={['Yes', 'No']}
                showMandatorySymbol={mappingFormSettings?.securityAllowance?.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.personalWebsite.show && (
            <GridItem>
              <ControlledTextInput
                name="personalWebsite"
                labelText="Personal Website"
                fullWidth
                id="personalWebsite"
                placeholder="Enter Personal Website"
                variant="outlined"
                autoComplete="personalWebsite"
                required={mappingFormSettings?.personalWebsite.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.resume.show && (
            <GridItem>
              <Label>{formLabel('CV', mappingFormSettings?.resume.mandatory)}</Label>
              {candidate?.mappedCandidateResumes?.map((resume: ICandidateResume, index: number) => (
                <Stack key={index} direction="row" spacing={2} mb={2}>
                  <WsTypography variant={WsTypoVariants.BODY1}>
                    {resume?.resumeFileOriginalName ?? ''}
                  </WsTypography>
                  <DownloadIcon
                    onClick={() => resumeAction && resumeAction('download', resume?.resumeLocation)}
                    sx={{ color: WsColors.TheBlues500, fontSize: '24px', cursor: 'pointer' }}
                  />
                  <VisibilityIcon
                    onClick={() => resumeAction && resumeAction('view', resume?.resumeLocation)}
                    sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                  />
                  <CloseIcon
                    onClick={() =>
                      resumeAction && resumeAction('removeCandidate', resume?.resumeLocation)
                    }
                    sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                  />
                </Stack>
              ))}
              <WsFileUpload
                multiple={false}
                onUpload={(file: File[]) => onUpload && onUpload(file)}
                name="resume"
                placeholderText=" "
                bgRequired={false}
                buttonText={'Upload File'}
                maxFileSize={3}
              />
            </GridItem>
          )}
          {mappingFormSettings?.socialLinks.show && (
            <GridItem>
              <ControlledSocialLinkInput name="socialLinks" />
            </GridItem>
          )}
          {mappingFormSettings?.messengerLinks.show && (
            <GridItem>
              <ControlledMessengerInput name="messengerLinks" />
            </GridItem>
          )}
          {mappingFormSettings?.skills.show && (
            <GridItem>
              <Label>{formLabel('Skills', mappingFormSettings?.skills.mandatory)}</Label>
              <FormTagsInput
                name="skills"
                placeholder="Enter Skills"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.skills.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.candidatePersonalId.show && (
            <GridItem>
              <ControlledTextInput
                name="candidatePersonalId"
                labelText={formLabel(
                  'Candidate ID',
                  mappingFormSettings?.candidatePersonalId.mandatory
                )}
                fullWidth
                id="candidatePersonalId"
                placeholder="Enter candidate id"
                variant="outlined"
                autoComplete="candidatePersonalId"
                required={mappingFormSettings?.candidatePersonalId.mandatory}
              />
            </GridItem>
          )}
        </>
      )}
    </>
  );
};

export default PersonalInfo;
